import { Fragment, useContext, useEffect, useRef, useState } from 'react';
import ImageLayer from 'ol/layer/Image';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import GeoJSON from 'ol/format/GeoJSON';
import { Stroke, Style } from 'ol/style';
import { Card, Checkbox, Col, Collapse, Form, Row } from 'antd';
import Select from 'react-select';
import { Button, Spinner } from 'react-bootstrap';
import CloseButton from 'antd/es/button';
import { CloseOutlined } from '@ant-design/icons';
import { BoundariesMap, NameShape } from '../../util/BoundaryMapData';
import { BoundaryData } from '../BoundaryData';
import JSZip from 'jszip';
import {
  getSLDForLayer,
  getSLDForLayerCatagorical,
  createArcGISVectorSource,
  attachArcGISOverview,
  createClippedArcGISVectorSource,
  queryArcGISFeaturesInGeometry,
  buildArcGISStyle,
  createArcGISTileLayer,
  exportArcGISTilesClipped,
} from '../utils';
import { ImageWMS } from 'ol/source';
import WPS from '../../lib/WPS';
import WCS from '../../lib/WCS';
import WFS from '../../lib/WFS';
// import { CaretDownOutlined } from '@ant-design/icons';
import { LayerStyleHelper } from '../../util/StyleHelper';
import CKAN from '../../lib/CKAN';
import { DownloadContext } from '../../context/DownloadContext';
import { isVersionGreater } from '../../util/VersionHelper';
import { formatTitle } from '../../util/TitleHelper';
import './LayerTreeControlPanel.css';
const LAYER_PANEL_CONTENT_WIDTH = 325;

const LayerTreeControlPanel = (props) => {
  const {
    map,
    layers,
    layersByUser,
    getGisService,
    selectedDatasets,
    access_token,
    getItemKey,
    opacities,
    layerColors,
    filterItems,
    filter_options,
    expand,
    setExpand,
    setClipLayer,
    selectedClipBoundary,
    setSelectedClipBoundary,
    selectedClipSubset,
    setSelectedClipSubset,
    selectedCustomClipKey,
    setSelectedCustomClipKey,
    clipEnabled,
    setClipEnabled,
    highlightClip,
    setHighlightClip,
  } = props;

  const {
    loading,
    setLoading,
    cancelLoading,
    setCancelLoading,
    includeItsActivity,
    setIncludeItsActivity,
    includeItsFootprint,
    setIncludeItsFootprint,
    errorLoadMsg,
    setErrorLoadMsg,
    errorMsg,
    setErrorMsg,
    successMsg,
    setSuccessMsg,
    successReportMsg,
    setSuccessReportMsg,
    abortControllerRef,
  } = useContext(DownloadContext);

  const [clipGeom, setClipGeom] = useState(null);
  const [itsActivityLatest, setItsActivityLatest] = useState(false);
  const [itsFootprintLatest, setItsFootprintLatest] = useState(false);
  const [latestItsActivityVersion, setLatestItsActivityVersion] = useState('');
  const [latestItsFootprintVersion, setLatestItsFootprintVersion] =
    useState('');
  const californiaExtent = [-380000, -562000, 540000, 450000];
  // const [open, setOpen] = useState(false);

  const [boundaryOptions, setBoundarOptions] = useState([]);
  const [subsetOptions, setSubsetOptions] = useState([]);
  const [customKeyOptions, setCustomKeyOptions] = useState([]);
  const [listLayer, setListLayer] = useState([]);

  const clipLayerRef = useRef();
  const originalWmsSourceRef = useRef(null);
  const currentWmsLayersRef = useRef(null);

  const handleCancel = () => {
    setCancelLoading(true);

    if (abortControllerRef.current) {
      abortControllerRef.current.abort(new Error('CANCELLED'));
    }
  };
  const checkCanceled = () => {
    if (cancelLoading) {
      throw new Error('CANCELLED');
    }
  };
  const getClipContext = (e) => e.target?.getRenderer?.()?.context || e.context;

  const preRenderClip = (e, geom) => {
    const ctx = getClipContext(e);
    if (!ctx || !geom) return;

    const frameState = e.frameState;
    if (!frameState) return;

    const pixelRatio = frameState.pixelRatio;
    ctx.save();
    ctx.beginPath();

    try {
      const coordinates = geom.getCoordinates();

      // Handle both Polygon and MultiPolygon
      const processPolygon = (coords) => {
        // Use exterior ring only (first array)
        const exteriorRing = coords[0];
        exteriorRing.forEach((coord, index) => {
          const px = map.getPixelFromCoordinate(coord);
          if (px[0] === undefined || px[1] === undefined) return;
          const x = px[0] * pixelRatio;
          const y = px[1] * pixelRatio;
          if (index === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        });
      };

      if (geom.getType() === 'Polygon') {
        processPolygon(coordinates);
      } else if (geom.getType() === 'MultiPolygon') {
        coordinates.forEach((polygon) => processPolygon(polygon));
      }

      ctx.closePath();
      ctx.clip();
    } catch (error) {
      // Leave the save() in place - postRenderRestore pops it.
      console.error('Error during prerender clip:', error);
    }
  };

  // Post-render restore function
  const postRenderRestore = (e) => {
    const ctx = getClipContext(e);
    if (ctx) {
      ctx.restore();
    }
  };

  const handleItsCheck = async (currentItsLayerName) => {
    const results = await CKAN.searchDatasets({
      searchString: '',
      collectionNames: 'California Interagency Treatment Tracking System',
      pillar: '',
      element: '',
    });
    if (results?.status) {
      console.error('Failed to fetch data', results);
    } else {
      results.forEach((data) => {
        let gis_service = getGisService(data);
        if (
          gis_service.layer_name.includes('ITS:activities') ||
          (gis_service.layer_name.includes('ITS:activity') &&
            gis_service.layer_name === currentItsLayerName)
        ) {
          if (
            isVersionGreater(
              data.version.split(' ')[1],
              latestItsActivityVersion?.split(' ')[1]
            )
          ) {
            setLatestItsActivityVersion(data.version);
          }
          setItsActivityLatest(true);
        } else if (
          gis_service.layer_name.includes('ITS:footprint') &&
          gis_service.layer_name === currentItsLayerName
        ) {
          setLatestItsFootprintVersion(data.version);
          setItsFootprintLatest(true);
        }
      });
    }
  };

  const cancellableFetch = async (url, options = {}) => {
    const controller = abortControllerRef.current;
    const signal = controller.signal;

    let response;
    try {
      response = await fetch(url, { ...options, signal });
    } catch (err) {
      if (err.name === 'AbortError') {
        throw new Error('CANCELLED');
      }
      throw err;
    }

    return response;
  };

  // const handleClipLayerUpdate = (newUtilLayer) => {
  //   setClipLayer((prev) =>
  //     prev.map((utilLayer) =>
  //       utilLayer.getProperties()?.name === 'clipLayer'
  //         ? newUtilLayer
  //         : utilLayer
  //     )
  //   );
  // };

  // initialize map once
  useEffect(() => {
    if (clipLayerRef.current) {
      return;
    }

    // clip vector layer
    const clipSource = new VectorSource({
      format: new GeoJSON(),
    });

    const clipStyle = new Style({
      stroke: new Stroke({
        color: 'red',
        width: 2,
      }),
    });
    clipLayerRef.current = new VectorLayer({
      zIndex: 999, // Keeps highlight on top
      source: clipSource,
      style: clipStyle,
      visible: true,
    });

    clipLayerRef.current.set('name', 'clipLayer');
    clipLayerRef.current.setVisible(true);
    setClipLayer([clipLayerRef.current]);

    // Cleanup
    return () => {
      if (originalWmsSourceRef.current)
        map.removeLayer(originalWmsSourceRef.current);
      if (currentWmsLayersRef.current)
        map.removeLayer(currentWmsLayersRef.current);
      if (clipLayerRef.current) map.removeLayer(clipLayerRef.current);
    };
  }, []);

  useEffect(() => {
    const layerList = [];
    const originalSourceList = [];
    const currentLayerList = [];
    // update layer num
    setItsActivityLatest(false);
    setItsFootprintLatest(false);
    map.getLayers().forEach((layer) => {
      const layerProps = layer.getProperties();
      const layerName = layerProps.layer_name;
      if (layerName !== undefined && layerName.search('boundary') !== -1) {
        let gis_service;
        selectedDatasets.forEach((dataset) => {
          const gis = getGisService(dataset);
          if (gis.layer_name === layerName) {
            gis_service = gis;
          }
        });
        if (gis_service) {
          const boundaryDataset = BoundariesMap.find(
            (boundary) => boundary.value.name === layerName
          );

          if (boundaryDataset) {
            layerList.push({
              name: boundaryDataset.label,
              data: boundaryDataset.value,
            });
          }
        }
      } else if (
        layerName !== undefined &&
        layerName.search('boundary') === -1
      ) {
        currentLayerList.push(layer);
        let gis_service;
        let item;
        selectedDatasets.forEach((dataset) => {
          const gis = getGisService(dataset);
          if (gis.layer_name === layerName) {
            gis_service = gis;
            item = dataset;
          }
        });
        // Tile layers have no WMS source to remember for the rebuild in
        // updateClipping - they're rebuilt straight from gis_service there.
        if (!item || !gis_service || gis_service.service_type === 'ArcGISTile')
          return;

        if (gis_service.data_type === 'raster') {
          let key = getItemKey(item);
          let sldBody = null;
          if (filterItems[key]?.values) {
            const minValue = filterItems[key].values[0].float_value;
            const maxValue = filterItems[key].values[1].float_value;
            const minColor = filterItems[key].values[0].color;
            const maxColor = filterItems[key].values[1].color;
            sldBody = getSLDForLayer(
              gis_service.layer_name,
              minColor,
              maxColor,
              maxValue,
              minValue,
              maxValue,
              minValue
            );
          }
          const imageLayer = new ImageWMS({
            url: gis_service.service_url,
            crossOrigin: 'anonymous',
            params: {
              LAYER_NAME: gis_service.layer_name,
              LAYERS: gis_service.layer_name,
              STYLES: undefined,
              TILED: true,
            },
            serverType: 'geoserver',
          });
          if (filterItems[key]?.values) {
            imageLayer.updateParams({ SLD_BODY: sldBody });
          } else if (gis_service.layer_name.search('rrk:') !== -1) {
            const sld = LayerStyleHelper(gis_service.layer_name);
            if (sld) {
              if (sld.includes('_std')) {
                layer.getSource().updateParams({
                  STYLES: sld,
                });
              } else {
                layer.getSource().updateParams({
                  LAYERS: sld,
                });
              }
            }
          }
          // );
          originalSourceList.push(imageLayer);
        } else {
          if (gis_service.service_type !== 'ArcGIS') {
            if (gis_service.layer_name.includes('ITS:')) {
              handleItsCheck(gis_service.layer_name);
            }
            originalSourceList.push(
              new ImageWMS({
                url: gis_service.service_url,
                crossOrigin: 'anonymous',
                params: {
                  LAYER_NAME: gis_service.layer_name,
                  LAYERS: gis_service.layer_name,
                  STYLES: undefined,
                  TILED: true,
                  // 'cql_filter': item.cql_filter
                },
              })
            );
          }
        }
      } else if (
        layerProps.name &&
        layersByUser?.find((layer) => {
          return layer !== layerProps.name;
        })
      ) {
        const geojsonFormat = new GeoJSON();
        const features = layer.getSource().getFeatures();
        const geojsonString = geojsonFormat.writeFeatures(features); // Returns XML string
        const geojsonObject = JSON.parse(geojsonString);

        const geoData = geojsonObject.features.map((location) => {
          return location;
        });
        if (!layerList.some((layer) => layer.name === layerProps.name))
          layerList.push({
            name: layerProps.name,
            data: geoData,
            crs: layerProps.crs,
          });
      }
    });
    originalWmsSourceRef.current = originalSourceList;
    setListLayer(layerList);

    if (
      selectedCustomClipKey &&
      selectedClipSubset &&
      selectedClipBoundary &&
      subsetOptions?.length === 0 &&
      customKeyOptions?.length === 0
    ) {
      handleSelectedDataset(selectedClipBoundary);
      handleCustomKeySelection(selectedCustomClipKey, selectedClipSubset);
    } else if (
      selectedClipSubset &&
      selectedClipBoundary &&
      subsetOptions?.length === 0
    ) {
      handleSelectedDataset(selectedClipBoundary, selectedClipSubset);
    }
    const boundaryList = layerList
      .filter((layer) => layer.name !== 'clipLayer')
      .map((value) => {
        return { value: value, label: value.name };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
    setBoundarOptions(boundaryList);
  }, [selectedDatasets, layersByUser, map.getLayers().getLength()]);

  const updateClipping = (enabled, geom) => {
    if (!map.getLayers()) return;
    [...map.getLayers().getArray()].forEach(async (layer) => {
      const layerProps = layer.getProperties();
      const layerName = layerProps.layer_name;
      if (layerName !== undefined && layerName.search('boundary') === -1) {
        const index = layer.getZIndex();
        const isVisible = layer.getVisible();
        let item;
        let gis_service;
        selectedDatasets.forEach((dataset) => {
          const gis = getGisService(dataset);
          if (gis?.layer_name === layerName) {
            gis_service = gis;
            item = dataset;
          }
        });
        if (!item || !gis_service) return;
        let key = getItemKey(item);
        layer.un('prerender', preRenderClip);
        layer.un('postrender', postRenderRestore);

        map.removeLayer(layer);

        if (gis_service.service_type === 'ArcGISTile') {
          // Pre-rendered tiles can't be filtered to the boundary like the
          // ArcGIS query below, so clipping is the visual mask alone.
          const newLayer = createArcGISTileLayer(gis_service, {
            visible: isVisible,
            opacity: opacities[key] ?? 0.7,
            zIndex: index,
          });
          if (enabled && geom) {
            newLayer.on('prerender', (e) => preRenderClip(e, geom));
            newLayer.on('postrender', postRenderRestore);
          }
          map.addLayer(newLayer);
          map.render();
          return;
        }

        if (gis_service.service_type === 'ArcGIS') {
          // ArcGIS layers have no cql_filter/SLD equivalent (that's a
          // GeoServer/WMS concept), so there's no attribute-filter rebuild
          // here - just the spatial clip: when enabled, restrict the
          // source to features intersecting the boundary instead of the
          // normal per-viewport-tile source, and attach the same visual
          // clip mask used for WMS layers.
          const srid = map
            .getView()
            .getProjection()
            .getCode()
            .split(/:(?=\d+$)/)
            .pop();
          const source =
            enabled && geom
              ? createClippedArcGISVectorSource(
                  gis_service.service_url,
                  geom,
                  srid
                )
              : createArcGISVectorSource(gis_service.service_url);
          const newLayer = new VectorLayer({
            visible: isVisible,
            opacity: opacities[key] ?? 0.7,
            layer_name: gis_service.layer_name,
            service_url: gis_service.service_url,
            source,
            style: buildArcGISStyle(gis_service.color_map, layerColors?.[key]),
            zIndex: index,
          });
          attachArcGISOverview(newLayer, gis_service.color_map);
          if (enabled && geom) {
            newLayer.on('prerender', (e) => preRenderClip(e, geom));
            newLayer.on('postrender', postRenderRestore);
          }
          map.addLayer(newLayer);
          newLayer.changed();
          map.render();
          return;
        }

        const original = originalWmsSourceRef.current.filter(
          (originalSource) => {
            return (
              originalSource.getParams().LAYER_NAME ==
              layer.getSource().getParams()?.LAYER_NAME
            );
          }
        )?.[0];
        if (!original) return;
        let sldBody = null;
        let sql = [];
        let final_sql;
        if (filterItems[key]?.values && filterItems[key]?.values.length == 2) {
          const minValue = filterItems[key].values[0].value;
          const maxValue = filterItems[key].values[1].value;
          const minColor = filterItems[key].values[0].color;
          const maxColor = filterItems[key].values[1].color;
          sldBody = getSLDForLayer(
            gis_service.layer_name,
            minColor,
            maxColor,
            maxValue,
            minValue,
            maxValue,
            minValue
          );
        } else if (filterItems[key]?.values) {
          const nilValue = await WCS.getDescribeCoverageNilValue(
            gis_service.layer_name
          );
          sldBody = getSLDForLayerCatagorical(
            gis_service.layer_name,
            filterItems[key]?.values,
            nilValue
          );
        } else if (filterItems[key]) {
          let values = filterItems[key];
          Object.keys(values)
            .filter(
              (filterKey) =>
                values[filterKey] !== undefined && values[filterKey].length > 0
            )
            .forEach((filterKey) => {
              let value = values[filterKey];
              let data_type = filter_options[key]?.filter(
                (e) => e.label == filterKey
              );
              if (data_type) {
                if (data_type.length > 0) {
                  data_type = data_type[0].type;
                }

                if (data_type == 'string') {
                  let string_values = '';
                  if (typeof value === 'string') {
                    string_values = "'" + value.split(',').join("', '") + "'";
                  } else {
                    string_values = "'" + value.join("', '") + "'";
                  }

                  sql.push(`${filterKey} IN (${string_values})`);
                } else if (data_type == 'number') {
                  sql.push(
                    `${filterKey} >= ${value[0]} AND ${filterKey} <= ${value[1]}`
                  );
                }
              } else {
                let string_values = '';
                string_values = "'" + value.join("' , '") + "'";
                sql.push(`${filterKey} IN (${string_values})`);
              }
            });
          final_sql = sql.join(' AND ');
          final_sql.replaceAll(' ', '+');
        }

        const newWmsSource = new ImageWMS({
          url: original.getUrl(),
          params: {
            LAYER_NAME: gis_service.layer_name,
            LAYERS: gis_service.layer_name,
            STYLES: undefined,
            TILED: true,
            _cache: Date.now(),
          },
          serverType: 'geoserver',
          ratio: 1,
          imageLoadFunction: function (image, src) {
            const img = image.getImage();
            img.onerror = function () {
              console.warn('Failed to load WMS image:', src);
            };
            img.src = src;
          },
        });
        layer = new ImageLayer({
          visible: isVisible,
          opacity: opacities[key] ?? 0.6,
          type: 'WMS',
          layer_name: gis_service.layer_name,
          service_url: gis_service.service_url,
          source: newWmsSource,
          zIndex: index,
        });
        if (final_sql) {
          layer.getSource().updateParams({ cql_filter: final_sql });
        }
        if (sldBody) {
          layer.getSource().updateParams({
            SLD_BODY: sldBody,
          });
        } else if (gis_service.layer_name.search('rrk:') !== -1) {
          const sld = LayerStyleHelper(gis_service.layer_name);
          if (sld) {
            if (sld.includes('_std')) {
              layer.getSource().updateParams({
                STYLES: sld,
              });
            } else {
              layer.getSource().updateParams({
                LAYERS: sld,
              });
            }
          }
        }

        if (enabled && geom) {
          layer.on('prerender', (e) => preRenderClip(e, geom));
          layer.on('postrender', postRenderRestore);
        }
        map.addLayer(layer);
      } else if (layerProps.name === 'itsHighlightLayer') {
        console.log('layerProps.name', layerProps.name);
      }
      layer.changed();
      map.render();
    });
  };

  // Handle county selection
  useEffect(() => {
    if (!clipLayerRef.current) return;
    setErrorMsg(new Set());
    setSuccessMsg(new Set());
    setErrorLoadMsg(new Set());
    setSuccessReportMsg(new Set());
    const wfsSource = clipLayerRef.current.getSource();
    if (selectedClipSubset) {
      // Use EPSG:3310 for both WFS and map
      if (typeof selectedClipSubset.value === 'object') {
        const format = new GeoJSON();
        const features = format.readFeatures(selectedClipSubset.value, {
          featureProjection: 'EPSG:3310',
          dataProjection: 'EPSG:3310',
        });
        wfsSource.clear();
        wfsSource.addFeatures(features);

        const geometry = features[0].getGeometry();

        setClipGeom(geometry);

        try {
          map.getView().fit(geometry.getExtent(), {
            padding: [40, 40, 40, 40],
            duration: 1000,
          });
        } catch (e) {
          console.error('Error fitting to extent:', e);
        }
      } else {
        const wfsUrl =
          'https://sparcal.sdsc.edu/geoserver/boundary/wfs?' +
          'service=WFS&version=1.1.0&request=GetFeature&' +
          'typename=' +
          encodeURIComponent(`${selectedClipBoundary.value.data.name}`) +
          '&outputFormat=application/json&' +
          'srsname=EPSG:3310&cql_filter=' +
          encodeURIComponent(`${selectedClipBoundary.value.data.label}`) +
          '=' +
          encodeURIComponent(`'${selectedClipSubset.label}'`);

        fetch(wfsUrl)
          .then((response) => {
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }
            return response.json();
          })
          .then((data) => {
            if (data.features && data.features.length > 0) {
              const format = new GeoJSON();
              const features = format.readFeatures(data, {
                featureProjection: 'EPSG:3310',
                dataProjection: 'EPSG:3310',
              });

              wfsSource.clear();
              wfsSource.addFeatures(features);

              const geometry = features[0].getGeometry();

              setClipGeom(geometry);

              try {
                map.getView().fit(geometry.getExtent(), {
                  padding: [40, 40, 40, 40],
                  duration: 1000,
                });
              } catch (e) {
                console.error('Error fitting to extent:', e);
              }
            } else {
              console.warn(
                'No features loaded for county:',
                selectedClipSubset
              );
              setErrorLoadMsg(
                'No boundary found for this county. Please check the county name or server configuration.'
              );
            }
          })
          .catch((error) => {
            console.error('WFS fetch error:', error);
            setErrorLoadMsg(`Failed to load county boundary: ${error.message}`);
          });
      }
    } else {
      wfsSource.clear();
      setClipGeom(null);
      updateClipping(false);
    }
  }, [selectedClipSubset]);

  useEffect(() => {
    updateClipping(clipEnabled, clipGeom);
  }, [clipEnabled, clipGeom, layersByUser, layers]);

  const handleErrorMessage = (newMessage) => {
    setErrorMsg((prev) => new Set(prev).add(newMessage));
  };
  const handleSuccessMessage = (newMessage) => {
    setSuccessMsg((prev) => new Set(prev).add(newMessage));
  };
  const handleSuccessReportMessage = (newMessage) => {
    setSuccessReportMsg((prev) => new Set(prev).add(newMessage));
  };

  const handleDownload = async () => {
    setErrorMsg(new Set());
    setSuccessMsg(new Set());
    setErrorLoadMsg(new Set());
    setSuccessReportMsg(new Set());
    abortControllerRef.current = new AbortController();

    if (!clipGeom) return alert('Select a boundary and a subset.');
    let base_url = process.env.REACT_APP_API_URL;
    setLoading(true);
    const zip = new JSZip();
    let layerList = [];
    let tier1 = [];
    const sleep = (ms) => new Promise((res) => setTimeout(res, ms));
    map.getLayers().forEach((layer) => {
      checkCanceled();
      const layerProps = layer.getProperties();
      const layerName = layerProps?.layer_name;
      let gis_service = null;
      let data = null;

      selectedDatasets.forEach((dataset) => {
        const gis = getGisService(dataset);
        if (layerName == gis.layer_name) {
          gis_service = gis;
          data = dataset;
        }
      });
      if (layerName && !layerName.includes('boundary')) {
        if (gis_service.dataset_collections[0]?.tier == 1) {
          tier1.push({
            gis_service: gis_service,
            title: data.title,
            layerName: layerName,
          });
        }
        layerList.push({
          name: layerName,
          gis_service: gis_service,
          dataset: data,
        });
      }
    });
    checkCanceled();

    try {
      // Gets ITS Reports on tier 1 datasets
      if (includeItsActivity || includeItsFootprint) {
        let layerData = [];
        tier1.map((report) => {
          const colorData = report.gis_service.color_map?.color_map_entries;
          if (!colorData) return;

          let min_value = colorData[0].float_value;
          let max_value = colorData[1].float_value;
          let min_value_color = colorData[0].color;
          let max_value_color = colorData[1].color;
          let key =
            report.gis_service.service_url +
            '/' +
            report.gis_service.layer_name;
          if (filterItems[key]?.values.length == 2) {
            min_value_color = filterItems[key].values[0].color;
            max_value_color = filterItems[key].values[1].color;
          } else if (filterItems[key]?.values.length > 0) {
            // Categorical
            const minObj = filterItems[key]?.values.reduce((min, curr) => {
              return Number(curr.value) < Number(min.value) ? curr : min;
            });
            const maxObj = filterItems[key]?.values.reduce((max, curr) => {
              return Number(curr.value) > Number(max.value) ? curr : max;
            });
            min_value_color = minObj.color;
            max_value_color = maxObj.color;
          }
          layerData.push({
            layer_name: report.layerName,
            layer_title: report.title,
            min_value: min_value,
            max_value: max_value,
            min_value_color: min_value_color,
            max_value_color: max_value_color,
          });
        });
        let backgroundLayersJson = [];
        if (layerData) {
          backgroundLayersJson = JSON.stringify(layerData);
        }
        checkCanceled();

        // Custom boundary ITS report
        if (
          selectedClipSubset.value &&
          selectedClipSubset.value.type == 'Feature'
        ) {
          const feature_value = selectedClipSubset.value;
          const geojsonFormat = new GeoJSON();
          const olFeature = geojsonFormat.readFeature(feature_value, {
            dataProjection: 'EPSG:3310',
            featureProjection: 'EPSG:4326',
          });

          const geojson = geojsonFormat.writeFeaturesObject([olFeature], {
            featureProjection: 'EPSG:4326', // Ensure it's in [lon, lat]
          });
          const complete = ['pdf', 'xlsx'].map(async (value) => {
            checkCanceled();
            let reports = [];
            if (includeItsActivity)
              reports.push('its_activity_report_for_geojson');
            if (includeItsFootprint)
              reports.push('its_footprint_report_for_geojson_region');

            for (const url of reports) {
              checkCanceled();
              const apiUrl = `${process.env.REACT_APP_API_URL}/Utility/${url}_20251027`;
              const reportParams = {
                geojson_str: JSON.stringify(geojson),
                region_name: `${selectedClipSubset.label}`,
                output_format: value,
                background_layers: layerData,
              };
              const response = await cancellableFetch(apiUrl, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify(reportParams),
              });
              checkCanceled();
              if (!response.ok) {
                if (url.includes('footprint')) {
                  handleErrorMessage('ITS Footprint Report');
                } else {
                  handleErrorMessage('ITS Activity Report');
                }
              } else {
                if (url.includes('footprint')) {
                  handleSuccessMessage('ITS Footprint Report');
                } else {
                  handleSuccessMessage('ITS Activity Report');
                }
                const blob = await response.blob();
                checkCanceled();
                let itsNameType = '';
                if (url.includes('footprint')) {
                  itsNameType = '_ITS_Footprint_Report';
                } else {
                  itsNameType = '_ITS_Activity_Report';
                }
                zip.file(
                  `${selectedClipBoundary.label + '_' + selectedClipSubset.label + itsNameType}.${value}`,
                  blob
                );
              }
            }
          });
          await Promise.all(complete);
        } else {
          const complete = ['pdf', 'xlsx'].map(async (value) => {
            checkCanceled();
            let reports = [];
            if (includeItsActivity) reports.push('its_activity_report');
            if (includeItsFootprint) reports.push('its_footprint_report');

            for (const url of reports) {
              checkCanceled();
              const apiUrl = `${process.env.REACT_APP_API_URL}/Utility/${url}_20251027?boundary_dataset_name=${encodeURIComponent(selectedClipBoundary.label)}&region_name=${selectedClipSubset.label}&output_format=${value}&background_layers=${encodeURIComponent(backgroundLayersJson)}`;
              const response = await cancellableFetch(apiUrl, {});
              checkCanceled();
              if (!response.ok) {
                if (url.includes('footprint')) {
                  handleErrorMessage('ITS Footprint Report');
                } else {
                  handleErrorMessage('ITS Activity Report');
                }
              } else {
                if (url.includes('footprint')) {
                  handleSuccessReportMessage(
                    `${latestItsFootprintVersion} ITS Footprint Report`
                  );
                } else {
                  handleSuccessReportMessage(
                    `${latestItsActivityVersion} ITS Activity Report`
                  );
                }
              }
              const blob = await response.blob();
              checkCanceled();
              let itsNameType = '';
              if (url.includes('activit')) {
                itsNameType = '_ITS_Activity_Report';
              } else {
                itsNameType = '_ITS_Footprint_Report';
              }
              zip.file(
                `${selectedClipBoundary.label + '_' + selectedClipSubset.label + itsNameType}.${value}`,
                blob
              );
              await sleep(200);
            }
          });
          await Promise.all(complete);
        }
      }

      // Gets CLM and ITS clip
      const fetchPromises = layerList.map(async (layer) => {
        let layerName = layer.name;
        const title = formatTitle(layer.dataset.title);
        if (layer.gis_service?.service_type === 'ArcGISTile') {
          // Cached tiles have no feature data to query, so export the map
          // image itself: tiles stitched and clipped to the boundary.
          try {
            const tiff = await exportArcGISTilesClipped({
              gis_service: layer.gis_service,
              clipGeometry: clipGeom,
              clipProjection: map.getView().getProjection(),
            });
            checkCanceled();
            if (!tiff) {
              setErrorLoadMsg((prev) =>
                new Set(prev).add(
                  `${title}: this layer has no map tiles inside the boundary - skipped.`
                )
              );
              return;
            }
            zip.file(`${layerName}.tif`, tiff);
            handleSuccessMessage(`${title}`);
          } catch (e) {
            console.error(e);
            handleErrorMessage(`${title}`);
          }
          return;
        }
        const arcgisData = layer.dataset.resources.find(
          (api) => api.format === 'ArcGIS Feature Service'
        );
        if (arcgisData) {
          // No WFS/WCS backend equivalent exists for ArcGIS - query the
          // service directly for whatever intersects the same clip
          // geometry used to draw/highlight the boundary on the map.
          if (!clipGeom) {
            handleErrorMessage(`${title}`);
            return;
          }
          try {
            const srid = map
              .getView()
              .getProjection()
              .getCode()
              .split(/:(?=\d+$)/)
              .pop();
            const { rawFeatures, truncated } =
              await queryArcGISFeaturesInGeometry({
                serviceUrl: arcgisData.url,
                clipGeometry: clipGeom,
                srid,
                format: 'geojson',
              });
            checkCanceled();
            if (truncated) {
              setErrorLoadMsg((prev) =>
                new Set(prev).add(
                  `${title}: more features in this boundary than the download limit - only the first ${rawFeatures.length.toLocaleString()} were included. Choose a smaller boundary to get everything.`
                )
              );
            }
            zip.file(
              `${layerName}.geojson`,
              JSON.stringify({
                type: 'FeatureCollection',
                features: rawFeatures,
              })
            );
            handleSuccessMessage(truncated ? `${title} (partial)` : `${title}`);
          } catch (e) {
            console.error(e);
            handleErrorMessage(`${title}`);
          }
          return;
        }
        const wfsData = layer.dataset.resources.find(
          (api) => api.format === 'WFS'
        );
        if (
          selectedClipSubset.value &&
          selectedClipSubset.value.type == 'Feature'
        ) {
          // Custom boundary for ITS
          if (wfsData) {
            const feature_value = selectedClipSubset.value;

            const geojsonFormat = new GeoJSON();
            const olFeature = geojsonFormat.readFeature(feature_value, {
              featureProjection: 'EPSG:3310',
              dataProjection: 'EPSG:3310',
            });
            const coords = olFeature.getGeometry().getCoordinates().flat();
            const coordsArray = coords[0].map((pt) => {
              return `${pt[0]} ${pt[1]} `;
            });
            const coordsString = coordsArray.join(' ');
            let response = null;
            if (layerName.includes('ITS')) {
              response = await WFS.getITSDownloadClip(
                wfsData.wfs_feature_id,
                coordsString
              );
            } else {
              response = await WFS.getCustomWFSDownloadClip(
                wfsData.wfs_feature_id,
                coordsString
              );
            }
            checkCanceled();

            if (!response.ok) {
              handleErrorMessage(
                `Failed to fetch ITS data for ${title}, status: ${response.status}`
              );
            } else {
              handleSuccessMessage(`${title}`);
            }
            const arrayblob = await response.blob();
            zip.file(`${layerName}.geojson`, arrayblob);
          } else {
            // Custom Boundary for CLM
            const feature_value = selectedClipSubset.value;
            const wcsData = layer.dataset.resources.find(
              (api) => api.format === 'WCS'
            );
            const response = await WCS.getWcsClipping(wcsData, feature_value);
            checkCanceled();
            if (!response.ok) {
              handleErrorMessage(`${title}`);
            } else {
              handleSuccessMessage(`${title}`);
              const blob = await response.blob();
              const arrayblob = await blob.arrayBuffer();
              zip.file(`${layer.dataset.title}.zip`, arrayblob, {
                binary: true,
              });
            }
          }
        } else {
          if (wfsData) {
            const response = await WPS.getDownload(
              layer.gis_service,
              selectedClipBoundary.value.data,
              selectedClipSubset.label
            );
            checkCanceled();
            if (!response.ok) {
              handleErrorMessage(
                `Failed to fetch layer ${title}, status: ${response.status}`
              );
            } else {
              handleSuccessMessage(`${title}`);
            }
            const arrayblob = await response.blob();
            zip.file(`${layerName}.geojson`, arrayblob);
          } else {
            try {
              const fileName = `${layerName}`;
              const encodedLayer = encodeURIComponent(layerName);
              const encodedDataset = encodeURIComponent(
                selectedClipBoundary.label
              );
              const encodedCounty = encodeURIComponent(
                selectedClipSubset.label
              );

              const fetchUrl = `${base_url}/download/download_layer/${encodedLayer}/${fileName}?vector_layer_name=${encodedDataset}&vector_column_filter=${encodedCounty}`;

              const response = await fetch(fetchUrl, {
                method: 'GET',
                headers: {
                  Accept: 'application/octet-stream',
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${access_token}`,
                },
              });
              checkCanceled();
              if (!response.ok) {
                handleErrorMessage(`${title}`);
              } else {
                handleSuccessMessage(`${title}`);
                const arrayblob = await response.blob();
                checkCanceled();
                zip.file(`${layerName}.zip`, arrayblob);
              }
            } catch (e) {
              console.error(e);
              handleErrorMessage(`${title} `);
            }
          }
        }
      });

      await Promise.all(fetchPromises);
      checkCanceled();
      const isEmpty = Object.keys(zip.files).length === 0;
      if (!isEmpty) {
        await zip
          .generateAsync({
            type: 'blob',
            streamFiles: true,
          })
          .then((zipBlob) => {
            const blobUrl = URL.createObjectURL(zipBlob);

            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = `${selectedClipSubset.label}.zip`;
            document.body.appendChild(link);
            link.click();
          });
      } else {
        alert('No files were found for downloading in the subset area.');
      }
      checkCanceled();
    } catch (err) {
      setErrorMsg(new Set());
      setSuccessMsg(new Set());
      setErrorLoadMsg(new Set());
      setSuccessReportMsg(new Set());
      setCancelLoading(false);
      setLoading(false);

      let alertMessage = 'Download failed';
      if (err.message === 'CANCELLED') {
        alertMessage = 'Download canceled.';
      }
      alert(alertMessage);
    }
    setLoading(false);
    setCancelLoading(false);
  };

  const handleSelectedDataset = (value, subsetValue) => {
    setSelectedClipBoundary(value);
    setSelectedClipSubset(null);
    setSelectedCustomClipKey(null);
    setSubsetOptions([]);
    setCustomKeyOptions([]);
    const options = listLayer
      .filter((layer) => {
        return layer.name === value.label;
      })
      .flatMap((data) => {
        // Catalog datasets
        if (data.name && BoundaryData[data.name]) {
          return BoundaryData[data.name].map((value) => {
            return { value: value, label: value };
          });
          // Custom Boundaries
        } else if (data.data.length > 0) {
          return data.data.map((area) => {
            const areas = Object.keys(area.properties)
              .filter((key) => NameShape.has(key))
              .map((key) => {
                return {
                  label: area.properties[key],
                  value: area,
                };
              });
            if (areas.length === 0) {
              const keys = Object.keys(area.properties).map((key) => {
                return { value: key, label: key };
              });
              setCustomKeyOptions(keys);
            } else return areas[0];
          });
        }
      })
      .sort((a, b) => {
        if (typeof a.label == 'string') return a.label.localeCompare(b.label);
        return a.label - b.label;
      });
    setSubsetOptions(options);
    if (subsetValue && subsetValue?.label && subsetValue?.value)
      setSelectedClipSubset(subsetValue);
  };
  const handleCustomKeySelection = (value, subsetValue) => {
    setSelectedCustomClipKey(value);
    setSelectedClipSubset(null);
    const options = listLayer
      .filter((layer) => {
        return layer.name === selectedClipBoundary.label;
      })
      .flatMap((data) => {
        if (data.data.length > 0) {
          return data.data.map((area) => {
            const areas = Object.keys(area.properties)
              .filter((key) => {
                return value.value === key;
              })
              .map((key) => {
                return {
                  label: area.properties[key],
                  value: area,
                };
              });
            return areas[0];
          });
        }
      })
      .sort((a, b) => {
        if (typeof a.label == 'string') return a.label.localeCompare(b.label);
        return a.label - b.label;
      });
    if (value && value?.label && value?.value) {
      setSelectedCustomClipKey(value);
    }
    if (subsetValue && subsetValue?.label && subsetValue?.value) {
      setSelectedClipSubset(subsetValue);
    }
    setSubsetOptions(options);
  };
  const handleSelectedSubset = (value) => {
    setSelectedClipSubset(value);
    setHighlightClip(true);
    setClipEnabled(true);
  };
  const handleClear = () => {
    setSelectedClipBoundary(null);
    setSelectedClipSubset(null);
    setSubsetOptions([]);
    setSelectedCustomClipKey(null);
    setCustomKeyOptions([]);
    clipLayerRef.current.setVisible(false);
  };

  return (
    <div className="container" style={{ padding: 0 }}>
      {props.tab == 1 ? (
        <Collapse
          style={{
            fontWeight: 'bold',
            width: LAYER_PANEL_CONTENT_WIDTH,
            backgroundColor: '#CEDBD6',
          }}
          activeKey={expand}
          onChange={(key) => {
            setExpand(key);
          }}
          items={[
            {
              key: 1,
              label: 'Visualization',
              children: (
                <Form name="ControlPanel1">
                  <Col>
                    <label htmlFor="clip-boundary-select-1">
                      Select Boundary Dataset
                    </label>
                    <Form.Item style={{ marginBottom: '8px' }}>
                      <Select
                        inputId="clip-boundary-select-1"
                        classNamePrefix="clip-select"
                        value={selectedClipBoundary}
                        placeholder="Select a Dataset"
                        onChange={handleSelectedDataset}
                        options={boundaryOptions}
                      ></Select>
                    </Form.Item>
                    {customKeyOptions.length > 0 && (
                      <>
                        <label htmlFor="clip-key-select-1">Select an ID</label>
                        <Form.Item style={{ marginBottom: '12px' }}>
                          <Select
                            inputId="clip-key-select-1"
                            classNamePrefix="clip-select"
                            showSearch
                            value={selectedCustomClipKey}
                            placeholder="Select a Key"
                            onChange={handleCustomKeySelection}
                            options={customKeyOptions}
                          ></Select>
                        </Form.Item>
                      </>
                    )}
                    <label htmlFor="clip-location-select-1">
                      Select Location from Dataset
                    </label>
                    <Form.Item style={{ marginBottom: '12px' }}>
                      <Select
                        inputId="clip-location-select-1"
                        classNamePrefix="clip-select"
                        showSearch
                        value={selectedClipSubset}
                        placeholder="Select a Location"
                        onChange={handleSelectedSubset}
                        options={subsetOptions}
                      ></Select>
                    </Form.Item>
                    <Button
                      className="btn_primary"
                      style={{ width: '100%' }}
                      onClick={handleClear}
                    >
                      Clear
                    </Button>
                    <hr />
                    <Row>
                      <h5>Visualization</h5>
                    </Row>
                    <Row style={{ marginBottom: '4px' }}>
                      <Checkbox
                        checked={highlightClip}
                        style={{
                          '--background-color': '#3F7C85',
                          '--border-color': '#2B2B2B',
                          borderColor: '#2B2B2B',
                        }}
                        onChange={() => {
                          setHighlightClip(!highlightClip);
                          clipLayerRef.current.setVisible(!highlightClip);
                        }}
                      >
                        Highlight Selected Location
                      </Checkbox>
                    </Row>
                    <Row style={{ marginBottom: '4px' }}>
                      <Checkbox
                        style={{
                          '--background-color': '#3F7C85',
                          '--border-color': '#2B2B2B',
                          borderColor: '#2B2B2B',
                        }}
                        checked={clipEnabled}
                        onChange={(e) => {
                          setClipEnabled(e.target.checked);
                        }}
                      >
                        Show Only Selected Location Data
                      </Checkbox>
                    </Row>
                    <Row style={{ marginBottom: '4px' }}>
                      <Button
                        className="btn_primary"
                        style={{ width: '100%' }}
                        onClick={() =>
                          clipGeom &&
                          map.getView().fit(clipGeom.getExtent(), {
                            padding: [40, 40, 40, 40],
                            duration: 500,
                          })
                        }
                      >
                        Zoom to Selected Area
                      </Button>
                    </Row>
                    <Row style={{ marginBottom: '4px' }}>
                      <Button
                        className="btn_primary"
                        style={{ width: '100%' }}
                        onClick={() => {
                          map.getView().fit(californiaExtent, {
                            padding: [20, 20, 20, 20],
                            duration: 1000,
                          });
                        }}
                      >
                        Zoom to California
                      </Button>
                    </Row>
                  </Col>
                </Form>
              ),
            },
          ]}
        ></Collapse>
      ) : (
        <Card>
          <strong>
            <Col>
              <label htmlFor="clip-boundary-select-2">
                Select Boundary Dataset
              </label>
              <Form.Item style={{ marginBottom: '8px' }}>
                <Select
                  inputId="clip-boundary-select-2"
                  classNamePrefix="clip-select"
                  value={selectedClipBoundary}
                  placeholder="Select a Dataset"
                  onChange={handleSelectedDataset}
                  options={boundaryOptions}
                ></Select>
              </Form.Item>
              {customKeyOptions.length > 0 && (
                <>
                  <label htmlFor="clip-key-select-2">Select an ID</label>
                  <Form.Item style={{ marginBottom: '12px' }}>
                    <Select
                      inputId="clip-key-select-2"
                      classNamePrefix="clip-select"
                      showSearch
                      value={selectedCustomClipKey}
                      placeholder="Select a Key"
                      onChange={handleCustomKeySelection}
                      options={customKeyOptions}
                    ></Select>
                  </Form.Item>
                </>
              )}
              <label htmlFor="clip-location-select-2">
                Select Location from Dataset
              </label>
              <Form.Item style={{ marginBottom: '12px' }}>
                <Select
                  inputId="clip-location-select-2"
                  classNamePrefix="clip-select"
                  showSearch
                  value={selectedClipSubset}
                  placeholder="Select a Location"
                  onChange={handleSelectedSubset}
                  options={subsetOptions}
                ></Select>
              </Form.Item>
              <Button
                className="btn_primary"
                style={{ width: '100%' }}
                onClick={handleClear}
              >
                Clear
              </Button>
              <hr />
              <Row>
                <h5>Reporting </h5>
              </Row>
              <Row style={{ marginBottom: '4px' }}>
                <Checkbox
                  checked={includeItsActivity}
                  style={{
                    '--background-color': '#3F7C85',
                    '--border-color': '#2B2B2B',
                    borderColor: '#2B2B2B',
                  }}
                  onChange={() => {
                    setIncludeItsActivity(!includeItsActivity);
                  }}
                  disabled={!itsActivityLatest}
                >
                  Include {latestItsActivityVersion} ITS Activity Report
                </Checkbox>
              </Row>
              <Row style={{ marginBottom: '4px' }}>
                <Checkbox
                  checked={includeItsFootprint}
                  style={{
                    '--background-color': '#3F7C85',
                    '--border-color': '#2B2B2B',
                    borderColor: '#2B2B2B',
                  }}
                  onChange={() => {
                    setIncludeItsFootprint(!includeItsFootprint);
                  }}
                  disabled={!itsFootprintLatest}
                >
                  Include {latestItsFootprintVersion} ITS Footprint Report
                </Checkbox>
              </Row>
              <Row style={{ marginBottom: '4px' }}>
                <Button
                  className="btn_primary"
                  style={{ width: '100%', marginBottom: '4px' }}
                  onClick={handleDownload}
                  disabled={loading}
                >
                  {loading && <Spinner animation="border" size="sm" />}
                  Download All Data in Selected Boundary Location
                </Button>
                <Button
                  variant="secondary"
                  style={{ width: '100%' }}
                  disabled={!loading || cancelLoading}
                  onClick={handleCancel}
                >
                  {cancelLoading && <Spinner animation="border" size="sm" />}
                  Cancel
                </Button>
              </Row>
              {(errorLoadMsg.size > 0 ||
                successMsg.size > 0 ||
                errorMsg.size > 0 ||
                successReportMsg.size > 0) &&
                !loading && (
                  <div
                    style={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <p style={{ fontSize: '12px', marginBottom: '0px' }}>
                      {errorLoadMsg.size > 0 &&
                        Array.from(errorLoadMsg).map((message, i) => {
                          return (
                            <Fragment key={'Error' + i}>
                              {message}
                              <br />
                            </Fragment>
                          );
                        })}
                      {successReportMsg.size > 0 && (
                        <>
                          Download report succeeded:
                          <br />
                          {Array.from(successReportMsg).map((message, i) => {
                            return (
                              <Fragment key={'Success' + i}>
                                {i + 1}. {message}
                                <br />
                              </Fragment>
                            );
                          })}
                          <br />
                        </>
                      )}
                      {successMsg.size > 0 && (
                        <>
                          Download dataset succeeded:
                          <br />
                          {Array.from(successMsg).map((message, i) => {
                            return (
                              <Fragment key={'Success' + i}>
                                {i + 1}. {message}
                                <br />
                              </Fragment>
                            );
                          })}
                          <br />
                        </>
                      )}
                      {errorMsg.size > 0 && (
                        <span style={{ color: 'red' }}>
                          Download failed: No data found in the selected region:
                          <br />
                          {Array.from(errorMsg).map((message, i) => {
                            return (
                              <Fragment key={'Error' + i}>
                                {i + 1}. {message}
                                <br />
                              </Fragment>
                            );
                          })}
                        </span>
                      )}
                    </p>
                    <CloseButton
                      icon={<CloseOutlined />}
                      size={'small'}
                      onClick={() => {
                        setErrorMsg(new Set());
                        setErrorLoadMsg(new Set());
                        setSuccessMsg(new Set());
                        setSuccessReportMsg(new Set());
                      }}
                      style={{ padding: '4px' }}
                    ></CloseButton>
                  </div>
                )}
            </Col>
          </strong>
        </Card>
      )}
    </div>
  );
};

export default LayerTreeControlPanel;
